import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserDisplayInfo, getEmailAvatarInitials, getSession } from '../../services/authService';
import {
  formatSlideStorageUsageLabel,
  slideStorageUsagePercent,
  type SlideImageStorageUsage,
} from '../../services/slideImageStorageService';
import {
  resolveUserPlanTier,
  SUBSCRIPTION_PASS_CHANGED_EVENT,
  type UserPlanTier,
} from '../../utils/subscriptionPassStorage';
import { clearWorkspaceCpLocalCaches } from '../../utils/clearWorkspaceCpLocalCaches';
import { clearProgramWorkspaceStorageCompletely } from '../../utils/programEditWorkspace';
import { usePaidPassGate } from '../../contexts/PaidPassGateContext';
import {
  formatPageZoomPercent,
  PAGE_ZOOM_CHANGED_EVENT,
  PAGE_ZOOM_MAX,
  PAGE_ZOOM_MIN,
  readPageZoom,
  stepPageZoom,
} from '../../utils/pageZoom';
import { readUiSoundEnabled, subscribeUiSoundEnabled, toggleUiSoundEnabled } from '../../utils/uiSound';
import AcademicYearSelector from './AcademicYearSelector';
import { initSelectedAcademicYear } from '../../utils/selectedAcademicYear';
import { openSameOriginWindow } from '../../utils/appDocumentTitle';
import { showPresentationScreenSettings } from '../common/PresentationScreenGate';
import { isPlatformAdminEmail } from '../../constants/platformAdmin';
import { fetchAmIPlatformManager } from '../../services/platformManagerService';
import {
  readAdminSidebarMenuVisiblePref,
  writeAdminSidebarMenuVisiblePref,
  ADMIN_SIDEBAR_MENU_VISIBLE_CHANGED_EVENT,
} from '../../utils/adminSidebarMenuPref';
import { maskEmailForDisplay } from '../../utils/loginEmailMask';
import { useAiAccess } from '../../hooks/useAiAccess';
import { formatCredits, formatResetDate } from '../../services/aiQuotaService';
import './UserAccountMenu.css';

const publicAssetUrl = (file: string) => `${process.env.PUBLIC_URL || ''}/${file}`;

const accountMenuIconMask = (file: string) => `url(${publicAssetUrl(file)})`;

const AccountMenuItemIcon: React.FC<{ file: string }> = ({ file }) => (
  <span
    className="cp-user-account-menu-item-icon"
    aria-hidden
    style={{ WebkitMaskImage: accountMenuIconMask(file), maskImage: accountMenuIconMask(file) }}
  />
);

interface UserAccountMenuProps {
  userDisplay: UserDisplayInfo;
  workspaceId: string;
  slideImageStorageUsage: SlideImageStorageUsage | null;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  onOpenProfile: () => void;
  onOpenClassSettings?: () => void;
  onOpenClassMembersSettings?: () => void;
  onOpenClassLessonAlarmSettings?: () => void;
  onOpenWorksheetTemplateRegistry?: () => void;
  onOpenTutorials?: () => void;
  onLogout: () => void;
}

const UserAccountMenu: React.FC<UserAccountMenuProps> = ({
  userDisplay,
  workspaceId,
  slideImageStorageUsage,
  theme,
  onToggleTheme,
  onOpenProfile,
  onOpenClassSettings,
  onOpenClassMembersSettings,
  onOpenClassLessonAlarmSettings,
  onOpenWorksheetTemplateRegistry,
  onOpenTutorials,
  onLogout,
}) => {
  const [open, setOpen] = React.useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const panelId = useId();
  const { isFeatureLocked, requireFeature } = usePaidPassGate();
  const isFreePass = isFeatureLocked('content_registry');
  const passLockClass = isFreePass ? ' is-pass-locked' : '';
  const [sessionEmail, setSessionEmail] = useState('');
  const [isPlatformManager, setIsPlatformManager] = useState(false);
  const [adminSidebarVisible, setAdminSidebarVisible] = useState(true);
  const [googleAvatarFailed, setGoogleAvatarFailed] = useState(false);
  const googleAvatarUrl = userDisplay.googleAvatarUrl?.trim() || '';

  const close = useCallback(() => setOpen(false), []);

  // 전역 학년도 선택을 서버와 맞춤(다음 학년도 이용권이 새로 생겼으면 한 번 자동 선택)
  useEffect(() => {
    const run = () => {
      void initSelectedAcademicYear().then((changed) => {
        // 다음 학년도 이용권으로 학년도가 자동 전환되면 화면을 새로 불러온다
        if (changed) window.location.reload();
      });
    };
    run();
    window.addEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, run);
    return () => window.removeEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, run);
  }, []);

  const accountEmail = useMemo(() => {
    const display = (userDisplay?.email ?? '').trim().toLowerCase();
    return display || sessionEmail;
  }, [userDisplay?.email, sessionEmail]);

  const isAdminUser = useMemo(
    () => isPlatformAdminEmail(accountEmail) || isPlatformAdminEmail(sessionEmail),
    [accountEmail, sessionEmail]
  );

  const canToggleAdminSidebar = isAdminUser || isPlatformManager;

  useEffect(() => {
    let cancelled = false;
    void getSession()
      .then((session) => {
        if (!cancelled) setSessionEmail((session?.user?.email ?? '').trim().toLowerCase());
      })
      .catch(() => {
        if (!cancelled) setSessionEmail('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isAdminUser) {
      setIsPlatformManager(false);
      return;
    }
    let cancelled = false;
    void fetchAmIPlatformManager()
      .then((v) => {
        if (!cancelled) setIsPlatformManager(v);
      })
      .catch(() => {
        if (!cancelled) setIsPlatformManager(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdminUser, accountEmail]);

  useEffect(() => {
    if (!accountEmail) {
      setAdminSidebarVisible(true);
      return;
    }
    setAdminSidebarVisible(readAdminSidebarMenuVisiblePref(accountEmail));
  }, [accountEmail]);

  useEffect(() => {
    const onPref = (ev: Event) => {
      const detail = (ev as CustomEvent<{ email?: string; visible?: boolean }>).detail;
      if (detail?.email && accountEmail && detail.email !== accountEmail) return;
      if (typeof detail?.visible === 'boolean') {
        setAdminSidebarVisible(detail.visible);
        return;
      }
      if (accountEmail) setAdminSidebarVisible(readAdminSidebarMenuVisiblePref(accountEmail));
    };
    window.addEventListener(ADMIN_SIDEBAR_MENU_VISIBLE_CHANGED_EVENT, onPref);
    return () => window.removeEventListener(ADMIN_SIDEBAR_MENU_VISIBLE_CHANGED_EVENT, onPref);
  }, [accountEmail]);

  useEffect(() => {
    setGoogleAvatarFailed(false);
  }, [googleAvatarUrl]);

  const [uiSoundOn, setUiSoundOn] = useState(() => readUiSoundEnabled());
  useEffect(() => subscribeUiSoundEnabled(setUiSoundOn), []);
  const handleToggleUiSound = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setUiSoundOn(toggleUiSoundEnabled());
  }, []);

  const handleToggleAdminSidebar = useCallback(() => {
    if (!accountEmail) return;
    const next = !adminSidebarVisible;
    writeAdminSidebarMenuVisiblePref(accountEmail, next);
    setAdminSidebarVisible(next);
  }, [accountEmail, adminSidebarVisible]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, close]);

  const pct = React.useMemo(() => {
    if (!slideImageStorageUsage) return 0;
    return slideStorageUsagePercent(
      slideImageStorageUsage.bytesUsed,
      slideImageStorageUsage.bytesLimit
    );
  }, [slideImageStorageUsage]);
  const label = slideImageStorageUsage
    ? formatSlideStorageUsageLabel(slideImageStorageUsage)
    : null;
  /** 한도를 넘거나 꽉 참(예: 프리 한도가 줄어 이미 더 쓰고 있는 계정) → 새 업로드는 서버에서 막힘 */
  const storageFull =
    !!slideImageStorageUsage &&
    slideImageStorageUsage.bytesLimit > 0 &&
    slideImageStorageUsage.bytesUsed >= slideImageStorageUsage.bytesLimit;
  const gaugeMods =
    storageFull || pct >= 98 ? ' is-critical' : pct >= 85 ? ' is-warning' : '';

  /** AI 크레딧(스토리지 게이지 아래). 권한 없음·로딩·RPC 실패(비관리자)는 숨김 */
  const { quota: aiQuota } = useAiAccess();
  const aiCredit = React.useMemo(() => {
    if (!aiQuota || !Object.values(aiQuota.permissions).some(Boolean)) return null;
    if (aiQuota.unlimited) {
      return {
        unlimited: true as const,
        label: aiQuota.used > 0 ? `AI 크레딧 무제한 · 이번 달 ${formatCredits(aiQuota.used)} 사용` : 'AI 크레딧 무제한',
      };
    }
    const granted = Math.max(0, aiQuota.granted);
    const used = Math.min(granted, Math.max(0, aiQuota.used));
    const usedPct = granted > 0 ? Math.min(100, Math.round((used / granted) * 100)) : 100;
    const exhausted = granted <= 0 || aiQuota.remaining <= 0;
    return {
      unlimited: false as const,
      label: `AI 크레딧 ${formatCredits(used)} / ${formatCredits(granted)} (${usedPct}%)`,
      usedPct: exhausted ? 100 : usedPct,
      exhausted,
      reset: `${formatResetDate(aiQuota.resetAt)} 충전`,
    };
  }, [aiQuota]);

  const [planTier, setPlanTier] = React.useState<UserPlanTier>(() => resolveUserPlanTier());
  const [pageZoom, setPageZoomLevel] = React.useState(() => readPageZoom());

  const refreshPlanTier = useCallback(() => {
    setPlanTier(resolveUserPlanTier());
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    refreshPlanTier();
    setPageZoomLevel(readPageZoom());
    const onChanged = () => refreshPlanTier();
    const onPageZoomChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ zoom?: number }>).detail;
      if (typeof detail?.zoom === 'number') {
        setPageZoomLevel(detail.zoom);
        return;
      }
      setPageZoomLevel(readPageZoom());
    };
    window.addEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, onChanged);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, onPageZoomChanged as EventListener);
    return () => {
      window.removeEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, onChanged);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, onPageZoomChanged as EventListener);
    };
  }, [open, refreshPlanTier]);

  const handlePageZoomOut = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setPageZoomLevel(stepPageZoom(-1));
  }, []);

  const handlePageZoomIn = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setPageZoomLevel(stepPageZoom(1));
  }, []);

  const hasTeam = userDisplay.hasActiveTeam === true;

  return (
    <div className="cp-user-account-menu-wrap" ref={wrapRef}>
      <div className="cp-topnav-account-trigger-row">
        <div
          className="cp-topnav-account-page-zoom"
          role="group"
          aria-label="화면 배율"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="cp-topnav-account-page-zoom-btn"
            onClick={handlePageZoomOut}
            disabled={pageZoom <= PAGE_ZOOM_MIN}
            aria-label="페이지 축소"
            title={`화면 축소 (${formatPageZoomPercent(pageZoom)})`}
          >
            −
          </button>
          <button
            type="button"
            className="cp-topnav-account-page-zoom-btn"
            onClick={handlePageZoomIn}
            disabled={pageZoom >= PAGE_ZOOM_MAX}
            aria-label="페이지 확대"
            title={`화면 확대 (${formatPageZoomPercent(pageZoom)})`}
          >
            +
          </button>
        </div>
        <button
          type="button"
          className={`cp-topnav-account-menu-burger-btn${open ? ' is-open' : ''}`}
          aria-expanded={open}
          aria-haspopup="true"
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? '계정 메뉴 닫기' : '계정 메뉴 열기'}
        >
          <span className="cp-topnav-account-burger-bar" aria-hidden />
          <span className="cp-topnav-account-burger-bar" aria-hidden />
          <span className="cp-topnav-account-burger-bar" aria-hidden />
        </button>
      </div>
      {open ? (
        <>
          <div className="cp-user-account-menu-backdrop" aria-hidden onClick={close} />
          <div
            id={panelId}
            className="cp-user-account-menu-panel"
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-user-account-menu-header">
              <div className="cp-user-account-menu-header-row">
                <div className="cp-user-account-menu-identity-cluster">
                  <div
                    className={`cp-user-account-menu-avatar${
                      googleAvatarUrl && !googleAvatarFailed ? ' has-photo' : ''
                    }`}
                    aria-hidden
                  >
                    {googleAvatarUrl && !googleAvatarFailed ? (
                      <img
                        src={googleAvatarUrl}
                        alt=""
                        draggable={false}
                        referrerPolicy="no-referrer"
                        onError={() => setGoogleAvatarFailed(true)}
                      />
                    ) : (
                      getEmailAvatarInitials(userDisplay.email)
                    )}
                  </div>
                  <span
                    className={`cp-user-account-menu-plan-tag cp-user-account-menu-plan-tag--${isAdminUser ? 'master' : planTier.toLowerCase()}`}
                    aria-label={`현재 플랜 ${isAdminUser ? 'MASTER' : planTier}`}
                  >
                    {isAdminUser ? 'MASTER' : planTier}
                  </span>
                </div>
                <button
                  type="button"
                  className="cp-user-account-menu-logout"
                  onClick={() => {
                    close();
                    onLogout();
                  }}
                >
                  로그아웃
                </button>
              </div>
              {hasTeam ? (
                <div className="cp-user-account-menu-team-role-row" role="group" aria-label="팀·역할">
                  <span className="cp-user-account-menu-team-pill">{userDisplay.teamDisplayName}</span>
                  <span className="cp-user-account-menu-team-sep" aria-hidden>
                    /
                  </span>
                  <span className="cp-user-account-menu-team-pill">{userDisplay.roleLabel}</span>
                </div>
              ) : (
                <div className="cp-user-account-menu-team-role-row" role="group" aria-label="소속">
                  <span className="cp-user-account-menu-team-pill">개인</span>
                </div>
              )}
              <div className="cp-user-account-menu-name">{userDisplay.displayName}</div>
              <div className="cp-user-account-menu-email">{maskEmailForDisplay(userDisplay.email)}</div>
              {label ? (
                <>
                  <div className="cp-user-account-menu-storage-row">
                    <span className="cp-user-account-menu-storage-label">{label}</span>
                  </div>
                  <div
                    className={`cp-user-account-menu-storage-gauge${gaugeMods}`}
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`슬라이드 이미지 스토리지 ${pct}% 사용`}
                  >
                    <div
                      className="cp-user-account-menu-storage-fill"
                      style={{
                        width: `${pct > 0 ? Math.max(2, pct) : 0}%`,
                      }}
                    />
                  </div>
                  {storageFull ? (
                    <p className="cp-user-account-menu-storage-over" role="status">
                      {slideImageStorageUsage && slideImageStorageUsage.bytesUsed > slideImageStorageUsage.bytesLimit
                        ? '저장 용량을 넘었어요. 이미지를 지워 한도 아래로 줄이면 다시 올릴 수 있어요.'
                        : '저장 용량이 가득 찼어요. 이미지를 지우면 다시 올릴 수 있어요.'}
                    </p>
                  ) : null}
                </>
              ) : null}
              {aiCredit ? (
                <div
                  className={`cp-user-account-menu-ai-credit${
                    aiCredit.unlimited ? ' is-unlimited' : aiCredit.exhausted ? ' is-exhausted' : ''
                  }`}
                >
                  <div className="cp-user-account-menu-storage-row cp-user-account-menu-ai-credit-row">
                    <span className="cp-user-account-menu-storage-label cp-user-account-menu-ai-credit-label">
                      {aiCredit.label}
                    </span>
                    {!aiCredit.unlimited ? (
                      <span
                        className="cp-user-account-menu-ai-credit-reset"
                        title="매월 1일 0시(한국 시간)에 다시 채워집니다."
                      >
                        {aiCredit.reset}
                      </span>
                    ) : null}
                  </div>
                  {aiCredit.unlimited ? (
                    <div
                      className="cp-user-account-menu-storage-gauge cp-user-account-menu-ai-credit-gauge"
                      role="img"
                      aria-label="AI 크레딧 무제한"
                    >
                      <div
                        className="cp-user-account-menu-storage-fill cp-user-account-menu-ai-credit-fill--rainbow"
                        style={{ width: '100%' }}
                      />
                    </div>
                  ) : (
                    <div
                      className="cp-user-account-menu-storage-gauge cp-user-account-menu-ai-credit-gauge"
                      role="progressbar"
                      aria-valuenow={aiCredit.usedPct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`이번 달 AI 크레딧 ${aiCredit.usedPct}% 사용`}
                    >
                      <div
                        className="cp-user-account-menu-storage-fill"
                        style={{ width: `${aiCredit.usedPct > 0 ? Math.max(2, aiCredit.usedPct) : 0}%` }}
                      />
                    </div>
                  )}
                </div>
              ) : null}
              <AcademicYearSelector />
              <button
                type="button"
                className="cp-user-account-menu-subscription-cta"
                onClick={() => {
                  close();
                  navigate('/subscription');
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/Ticket.svg`}
                  alt=""
                  className="cp-user-account-menu-subscription-cta-icon"
                  aria-hidden
                />
                이용권 구매 및 관리
              </button>
              {onOpenTutorials ? (
                <button
                  type="button"
                  className="cp-user-account-menu-subscription-cta cp-user-account-menu-tutorial-cta"
                  onClick={() => {
                    close();
                    onOpenTutorials();
                  }}
                >
                  <AccountMenuItemIcon file="Question.svg" />
                  튜토리얼
                </button>
              ) : null}
            </div>
            <div className="cp-user-account-menu-divider" role="separator" />
            <button
              type="button"
              role="menuitem"
              className="cp-user-account-menu-item cp-user-account-menu-item--with-icon cp-user-account-menu-item-settings"
              onClick={() => {
                close();
                onOpenProfile();
              }}
            >
              <AccountMenuItemIcon file="picprofile.svg" />
              프로필 및 팀 설정
            </button>
            {onOpenClassSettings || onOpenClassMembersSettings ? (
              <div className="cp-user-account-menu-divider" role="separator" />
            ) : null}
            {onOpenClassSettings ? (
              <button
                type="button"
                role="menuitem"
                className="cp-user-account-menu-item cp-user-account-menu-item--with-icon cp-user-account-menu-item-class-settings"
                onClick={() => {
                  close();
                  onOpenClassSettings();
                }}
              >
                <AccountMenuItemIcon file="class.svg" />
                클래스 등록 및 설정
              </button>
            ) : null}
            {onOpenClassMembersSettings ? (
              <button
                type="button"
                role="menuitem"
                className="cp-user-account-menu-item cp-user-account-menu-item--with-icon cp-user-account-menu-item-class-members"
                onClick={() => {
                  close();
                  onOpenClassMembersSettings();
                }}
              >
                <AccountMenuItemIcon file="members.svg" />
                클래스 멤버 설정
              </button>
            ) : null}
            {onOpenClassLessonAlarmSettings ? (
              <>
                <div className="cp-user-account-menu-divider" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  className="cp-user-account-menu-item cp-user-account-menu-item--with-icon cp-user-account-menu-item-class-lesson-alarm"
                  onClick={() => {
                    close();
                    onOpenClassLessonAlarmSettings();
                  }}
                >
                  <AccountMenuItemIcon file="alram.svg" />
                  수업 알람 설정
                </button>
              </>
            ) : null}
            <button
              type="button"
              role="menuitem"
              className="cp-user-account-menu-item cp-user-account-menu-item--with-icon cp-user-account-menu-item-presentation-screen"
              onClick={() => {
                close();
                showPresentationScreenSettings();
              }}
            >
              <AccountMenuItemIcon file="slide.svg" />
              보조 모니터 프레젠 설정
            </button>
            <div className="cp-user-account-menu-divider" role="separator" />
            <button
              type="button"
              role="menuitem"
              className={`cp-user-account-menu-item cp-user-account-menu-item--with-icon cp-user-account-menu-item-slide-registry${passLockClass}`}
              aria-disabled={isFreePass || undefined}
              onClick={() => {
                if (requireFeature('content_registry')) {
                  close();
                  return;
                }
                close();
                const sid = workspaceId.trim();
                if (!sid) return;
                const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
                const to = `${basePath}/program-content-slide?registry=1&workspaceId=${encodeURIComponent(
                  sid
                )}&mode=edit`;
                const url =
                  typeof window !== 'undefined' && window.location?.origin
                    ? `${window.location.origin}${to}`
                    : to;
                openSameOriginWindow(url);
              }}
            >
              <AccountMenuItemIcon file="slide.svg" />
              슬라이드 페이지 등록
            </button>
            <button
              type="button"
              role="menuitem"
              className={`cp-user-account-menu-item cp-user-account-menu-item--with-icon cp-user-account-menu-item-slide-layout-registry${passLockClass}`}
              aria-disabled={isFreePass || undefined}
              onClick={() => {
                if (requireFeature('content_registry')) {
                  close();
                  return;
                }
                close();
                const sid = workspaceId.trim();
                if (!sid) return;
                const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
                const to = `${basePath}/program-content-slide?registry=1&registryKind=layout&workspaceId=${encodeURIComponent(
                  sid
                )}&mode=edit`;
                const url =
                  typeof window !== 'undefined' && window.location?.origin
                    ? `${window.location.origin}${to}`
                    : to;
                openSameOriginWindow(url);
              }}
            >
              <AccountMenuItemIcon file="slide.svg" />
              슬라이드 레이아웃 등록
            </button>
            <button
              type="button"
              role="menuitem"
              className={`cp-user-account-menu-item cp-user-account-menu-item--with-icon cp-user-account-menu-item-worksheet-template-registry${passLockClass}`}
              aria-disabled={isFreePass || undefined}
              onClick={() => {
                if (requireFeature('content_registry')) {
                  close();
                  return;
                }
                close();
                const sid = workspaceId.trim();
                if (!sid) {
                  window.alert('학교(워크스페이스)를 선택한 뒤 다시 시도해 주세요.');
                  return;
                }
                onOpenWorksheetTemplateRegistry?.();
              }}
            >
              <AccountMenuItemIcon file="slide.svg" />
              활동지 레이아웃 등록
            </button>
            <div className="cp-user-account-menu-divider" role="separator" />
            <button
              type="button"
              role="menuitem"
              className="cp-user-account-menu-item cp-user-account-menu-item-clear-cache"
              onClick={() => {
                close();
                const sid = workspaceId.trim();
                if (!sid) {
                  window.alert('학교(워크스페이스)를 선택한 뒤 다시 시도해 주세요.');
                  return;
                }
                const ok = window.confirm(
                  '로컬 데이터를 초기화합니다.\n\n' +
                    '삭제 대상:\n' +
                    '- 프로그램 편집 초안(IndexedDB)·클립보드\n' +
                    '- 현재 학교의 로컬 캐시\n\n' +
                    '미저장 프로그램 편집은 사라집니다.\n' +
                    '로그인·학교·학년 선택·테마는 유지되고, DB 데이터는 그대로입니다.\n' +
                    '페이지를 새로고침한 뒤 서버에서 다시 받아옵니다.\n\n' +
                    '데이터가 꼬였을 때 사용하세요. 계속할까요?'
                );
                if (!ok) return;
                void clearProgramWorkspaceStorageCompletely().then(() => {
                  clearWorkspaceCpLocalCaches(sid);
                  window.location.reload();
                });
              }}
            >
              로컬 데이터 초기화
            </button>
            {canToggleAdminSidebar ? (
              <div
                className="cp-user-account-menu-item cp-user-account-menu-item-clear-cache cp-user-account-menu-admin-toggle"
                role="menuitem"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="cp-user-account-menu-admin-toggle-label">관리자 메뉴</span>
                <button
                  type="button"
                  className={`team-auto-approve-toggle cp-user-account-menu-admin-toggle-btn${
                    adminSidebarVisible ? ' on' : ''
                  }`}
                  aria-pressed={adminSidebarVisible}
                  aria-label={
                    adminSidebarVisible
                      ? '관리자 메뉴 표시 끄기'
                      : '관리자 메뉴 표시 켜기'
                  }
                  title={
                    adminSidebarVisible
                      ? '사이드바 관리자 메뉴 숨기기'
                      : '사이드바 관리자 메뉴 보이기'
                  }
                  onClick={handleToggleAdminSidebar}
                >
                  <span
                    className="team-auto-approve-toggle-slider"
                    data-on={!adminSidebarVisible}
                  >
                    OFF
                  </span>
                  <span
                    className="team-auto-approve-toggle-slider"
                    data-on={adminSidebarVisible}
                  >
                    ON
                  </span>
                </button>
              </div>
            ) : null}
            <div className="cp-user-account-menu-divider" role="separator" />
            <div
              className="cp-user-account-menu-item cp-user-account-menu-item-clear-cache cp-user-account-menu-admin-toggle cp-user-account-menu-sound-toggle"
              role="menuitem"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="cp-user-account-menu-admin-toggle-label cp-user-account-menu-sound-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M11 5 6 9H2v6h4l5 4V5z" />
                  {uiSoundOn ? (
                    <>
                      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                      <path d="M19 5a10 10 0 0 1 0 14" />
                    </>
                  ) : (
                    <>
                      <line x1="16" y1="9" x2="22" y2="15" />
                      <line x1="22" y1="9" x2="16" y2="15" />
                    </>
                  )}
                </svg>
                효과음
              </span>
              <button
                type="button"
                className={`team-auto-approve-toggle cp-user-account-menu-admin-toggle-btn${uiSoundOn ? ' on' : ''}`}
                aria-pressed={uiSoundOn}
                aria-label={uiSoundOn ? '효과음 끄기' : '효과음 켜기'}
                title={uiSoundOn ? '효과음 끄기' : '효과음 켜기'}
                onClick={handleToggleUiSound}
              >
                <span className="team-auto-approve-toggle-slider" data-on={!uiSoundOn}>
                  OFF
                </span>
                <span className="team-auto-approve-toggle-slider" data-on={uiSoundOn}>
                  ON
                </span>
              </button>
            </div>
            <div className="cp-user-account-menu-footer">
              <div className="cp-user-account-menu-theme" role="group" aria-label="화면 테마">
                <div className="cp-user-account-menu-theme-segment">
                  <button
                    type="button"
                    className={`cp-user-account-menu-theme-opt${theme === 'light' ? ' is-active' : ''}`}
                    onClick={() => {
                      if (theme === 'dark') onToggleTheme();
                    }}
                  >
                    일반
                  </button>
                  <button
                    type="button"
                    className={`cp-user-account-menu-theme-opt${theme === 'dark' ? ' is-active' : ''}`}
                    onClick={() => {
                      if (theme === 'light') onToggleTheme();
                    }}
                  >
                    다크
                  </button>
                </div>
              </div>
              <div
                className="cp-user-account-menu-page-zoom"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="cp-user-account-menu-page-zoom-btn"
                  onClick={handlePageZoomOut}
                  disabled={pageZoom <= PAGE_ZOOM_MIN}
                  aria-label="페이지 축소"
                >
                  −
                </button>
                <div className="cp-user-account-menu-page-zoom-percent" aria-live="polite">
                  {formatPageZoomPercent(pageZoom)}
                </div>
                <button
                  type="button"
                  className="cp-user-account-menu-page-zoom-btn"
                  onClick={handlePageZoomIn}
                  disabled={pageZoom >= PAGE_ZOOM_MAX}
                  aria-label="페이지 확대"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default UserAccountMenu;
